import { generateClient } from "aws-amplify/api";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import type { Subscription } from "rxjs";

import * as queries from "~/common/gqls/appsync/generated-query/queries";
import * as subscriptions from "~/common/gqls/appsync/generated-query/subscriptions";
import type { Message } from "~/common/gqls/appsync/generated-types/appsync";

import type { JobRunLogResult } from "./types";

const insertMessage = (setLogs: Dispatch<SetStateAction<Message[]>>, msg: Message) => {
  setLogs((exLogs) => {
    // do not insert duplicate messages
    if (exLogs.find((log) => log.messageId === msg.messageId)) {
      return exLogs;
    }

    // insert message in order (by messageId)
    const i = exLogs.findLastIndex((l) => l.messageId < msg.messageId);
    let newLogs: Message[] = [];
    if (i === -1) {
      newLogs = [...exLogs, msg];
    } else {
      newLogs = [...exLogs.slice(0, i + 1), msg, ...exLogs.slice(i + 1)];
    }

    // eslint-disable-next-line no-console
    console.log(`new log message: ${i + 1}: ${msg.messageId}`);

    return newLogs;
  });
};

const queryWithPagination = (jobRunId: string, callback: (items: Message[]) => void) => {
  const client = generateClient();
  const request = client.graphql({
    query: queries.queryMessages,
    variables: { jobRunId },
  });

  const doRequest = (req: typeof request) =>
    new Promise((resolve, reject) => {
      req
        .then((result) => {
          if (result.data.queryMessages) {
            callback(result.data.queryMessages.items);
            if (result.data.queryMessages.nextToken) {
              const pageReq = client.graphql({
                query: queries.queryMessages,
                variables: {
                  jobRunId,
                  nextToken: result.data.queryMessages.nextToken,
                },
              });
              return doRequest(pageReq);
            }
            return resolve(true);
          }
          return resolve(true);
        })
        .catch((error: unknown) => reject(error));
    });

  return doRequest(request);
};

export const useJobRunQueryMessages = (jobRunId: string, { enabled }: { enabled: boolean }): JobRunLogResult => {
  const logSource = `dynamodb: jobRunId=${jobRunId}`;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [logs, setLogs] = useState<Message[]>([]);

  useEffect(() => {
    let subscription: Subscription | undefined;

    if (!enabled) {
      return () => {};
    }

    queryWithPagination(jobRunId, (msgs) => msgs.forEach((msg) => insertMessage(setLogs, msg)))
      .then(() => {
        setLoading(false);
        subscription = generateClient()
          .graphql({
            query: subscriptions.tailLogMessages,
            variables: { jobRunId },
          })
          .subscribe({
            next: (event) => {
              const { data } = event;

              // eslint-disable-next-line no-console
              console.log(`tailLogMessages: next: ${JSON.stringify(data)}`);

              if (data.tailLogMessages) {
                const msg = data.tailLogMessages;
                insertMessage(setLogs, msg);
              }
            },
            error: (e) => {
              // eslint-disable-next-line no-console
              console.error(`tailLogMessages: error: ${e}`);
              setError(e);
            },
            complete: () => {
              // eslint-disable-next-line no-console
              console.log(`tailLogMessages: complete: jobRunId=${jobRunId}`);
            },
          });

        // eslint-disable-next-line no-console
        console.log(`tailLogMessages: subscribed: jobRunId=${jobRunId}`);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(`queryMessages: error: ${e}`);
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
      });

    return () => {
      // eslint-disable-next-line no-console
      console.log(`tailLogMessages: unsubscribed: jobRunId=${jobRunId}`);
      return subscription?.unsubscribe();
    };
  }, [jobRunId, enabled]);

  if (!enabled) {
    return { loading: false, error: null, data: [], logSource };
  }

  return { loading, data: logs, error, logSource };
};
